import { TranslateService } from "@ngx-translate/core";
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
} from "@angular/core";
import { BehaviorSubject, Observable, Observer } from "rxjs";
import { map } from "rxjs/operators";
import { Ticket } from "../../../model/ticket";
import { TicketService } from "../../../services/ticket.service";
import { AuthService } from "../../../auth.service";
import { Router } from "@angular/router";
import { NavigationHelper } from "../../../services/navigation.helper";
import { BranchService } from "../../../services/branch.service";
import { Navigation } from "../../../model/navigation";
import { SingleChoice } from "../../../model/singlechoice";
import { Labeler } from "../../../model/labeler";
import { RecurrenceService } from "../../../services/recurrence.service";
import { MultiActionService } from "../../../services/multi-action.service";

@Component({
    selector: "ticket-by-branch",
    templateUrl: "./ticket-by-branch.component.html",
    styleUrls: ["./ticket-by-branch.component.css"],
})
export class TicketByBranchComponent implements OnInit {
    @Input() isMobile = false;
    @Output() ticketSelected = new EventEmitter<number>();
    @Output() createNewTicket = new EventEmitter<void>();

    branchIds: number[] = [];
    branchTickets = new Map<number, Ticket[]>();
    branchPages = new Map<number, number>();
    branchTotalPages = new Map<number, number>();
    branchAllIds = new Map<number, number[]>();
    activeBranchIndex: number | null = null;
    selectedTicket: Ticket | null = null;
    /** Whether any ticket is checked: the bulk actions stay locked until one is (#173). */
    hasSelection!: Observable<boolean>;
    pageSize = 10;

    constructor(
        public ticketService: TicketService,
        private auth: AuthService,
        private router: Router,
        private nav: NavigationHelper,
        private branchService: BranchService,
        private multiActionService: MultiActionService,
        private cdr: ChangeDetectorRef,
        private recurrenceService: RecurrenceService,
        private translate: TranslateService
    ) { }

    ngOnInit(): void {
        // the headings name the locations: fetch them, and draw the headings
        // again once they are in, or they keep saying "…"
        this.branchService.makeLabel(0).subscribe();
        // and put the sections in order, which needs the locations too
        this.branchService.branchesObservable.subscribe(() => {
            this.sortBranches();
            this.cdr.markForCheck();
        });
        this.hasSelection = this.ticketService.selectTickets.pipe(map((tickets) => tickets.length > 0));
        const token = this.auth.token;

        this.ticketService.resultsByBranch.subscribe((branchMap) => {
            this.branchIds = Array.from(branchMap.keys());
            this.sortBranches();
            this.branchAllIds = branchMap;
            this.branchTickets.clear();
            this.branchPages.clear();
            this.branchTotalPages.clear();

            const nav = this.nav.freshNavigation;
            if (nav.id === "by-branch" && nav.action) {
                const urlBranchId = parseInt(nav.action, 10);
                const idx = this.branchIds.indexOf(urlBranchId);
                this.activeBranchIndex = idx >= 0 ? idx : null;
                if (idx >= 0) {
                    const page = (nav.fragmentTopic === "page" && nav.fragmentId)
                        ? parseInt(nav.fragmentId, 10)
                        : 0;
                    this.loadBranchPage(urlBranchId, page);
                }
            } else {
                this.activeBranchIndex = null;
            }

            for (const branchId of this.branchIds) {
                const ids = branchMap.get(branchId) || [];
                this.branchTotalPages.set(
                    branchId,
                    Math.ceil(ids.length / this.pageSize)
                );
                this.branchPages.set(branchId, 0);
            }
            this.cdr.detectChanges();
        });

        this.ticketService.searchByBranch(token).subscribe();
    }

    /**
     * A section's heading: abbreviation and name, or just the name where there is
     * no abbreviation. Tickets without a location, and those whose location is
     * no longer in the list (archived or removed), say so instead of "-," (#173).
     */
    branchName(branchId: number): string {
        if (!branchId) {
            return this.translate.instant("branches.none");
        }
        // the locations still coming in: not unknown, just not there yet
        if (!this.branchService.branchesData?.length) {
            return "…";
        }
        const branch = this.branchService.branchesData.find((branch) => branch.id === branchId);
        if (!branch) {
            return this.translate.instant("branches.unknown", { id: branchId });
        }
        const abbreviation = branch.abbreviation?.trim();
        return abbreviation ? `${abbreviation}, ${branch.name}` : branch.name;
    }

    branchTicketCount(branchId: number): number {
        return (this.branchAllIds.get(branchId) || []).length;
    }

    /**
     * The sections in order (#173): the locations with an abbreviation by it,
     * then by name; then those without one, by name; then those no longer in
     * the list, by number; and the tickets without a location last. The open
     * section stays open, found again by its location rather than its place,
     * since the locations can come in after the results and move it.
     */
    private sortBranches(): void {
        const open = this.activeBranchIndex !== null ? this.branchIds[this.activeBranchIndex] : null;
        const branches = new Map((this.branchService.branchesData ?? []).map((b) => [b.id, b]));
        const byText = (a: string, b: string) => a.localeCompare(b, "nl", { sensitivity: "base", numeric: true });

        const rank = (id: number) => {
            if (!id) return 3;
            const branch = branches.get(id);
            if (!branch) return 2;
            return branch.abbreviation?.trim() ? 0 : 1;
        };

        this.branchIds.sort((a, b) => {
            const ra = rank(a), rb = rank(b);
            if (ra !== rb) return ra - rb;
            const ba = branches.get(a), bb = branches.get(b);
            if (ra === 0) {
                return byText(ba!.abbreviation.trim(), bb!.abbreviation.trim()) || byText(ba!.name ?? "", bb!.name ?? "");
            }
            if (ra === 1) {
                return byText(ba!.name ?? "", bb!.name ?? "");
            }
            return a - b;
        });

        if (open !== null) {
            const index = this.branchIds.indexOf(open);
            this.activeBranchIndex = index >= 0 ? index : null;
        }
    }

    onTabOpen(event: any): void {
        const branchId = this.branchIds[event.index];
        this.activeBranchIndex = event.index;
        this.router.navigate([`tickets/by-branch/${branchId}`]);
        if (!this.branchTickets.has(branchId)) {
            this.loadBranchPage(branchId, 0);
        }
    }

    onTabClose(): void {
        this.activeBranchIndex = null;
    }

    loadBranchPage(branchId: number, page: number): void {
        const allIds = this.branchAllIds.get(branchId) || [];
        const start = page * this.pageSize;
        const end = Math.min(start + this.pageSize, allIds.length);
        const pageIds = allIds.slice(start, end);

        if (pageIds.length === 0) return;

        const token = this.auth.token;
        this.ticketService.fetchTickets(token, pageIds).subscribe((tickets) => {
            this.branchTickets.set(branchId, tickets);
            this.branchPages.set(branchId, page);
            this.loadRecurringMarkers();
            this.cdr.detectChanges();
        });
    }

    getTickets(branchId: number): Ticket[] {
        return this.branchTickets.get(branchId) || [];
    }

    /**
     * Vraagt voor alle getoonde vestigingen in een keer op welke tickets
     * terugkerend zijn, zodat de rijen hun markering kunnen tonen.
     */
    private loadRecurringMarkers(): void {
        const ids: number[] = [];

        for (const branchId of this.branchIds) {
            for (const ticket of this.getTickets(branchId)) {
                if (ticket.id && ticket.id > 0) {
                    ids.push(ticket.id);
                }
            }
        }
        this.recurrenceService.loadForTickets(this.auth.token, ids);
    }

    getCurrentPage(branchId: number): number {
        return this.branchPages.get(branchId) || 0;
    }

    getTotalPages(branchId: number): number {
        return this.branchTotalPages.get(branchId) || 0;
    }

    goToPage(branchId: number, page: number): void {
        const total = this.getTotalPages(branchId);
        if (page < 0 || page >= total) return;
        this.loadBranchPage(branchId, page);
        const h = this.nav.history;
        if (h.length > 0 && /by-branch/.test(h[h.length - 1])) {
            h.splice(h.length - 1, 1);
        }
        const path = page > 0
            ? `tickets/by-branch/${branchId}/page/${page}`
            : `tickets/by-branch/${branchId}`;
        this.router.navigate([path]);
    }

    select(ticket: Ticket): void {
        this.selectedTicket = ticket;
        if (ticket && ticket.id) {
            this.ticketService.select(ticket, true);
            this.ticketSelected.emit(ticket.id!);
            this.router
                .navigate([`tickets/${ticket.id}/fragmentedit/comment/-9`])
                .then(() => {
                    this.nav.freshNavigation;
                });
        }
    }

    toggleSelect(ticket: Ticket): void {
        this.ticketService.toggleTicketSelection(ticket);
    }

    isSelected(ticket: Ticket): boolean {
        return this.ticketService.isTicketSelected(ticket);
    }

    createTicket(): void {
        this.createNewTicket.emit();
        const newTicket = new Ticket(
            Navigation.NEW,
            "",
            "",
            "",
            0,
            "",
            0,
            0,
            "",
            0,
            0,
            "",
            0,
            new Date(),
            "",
            false
        );
        this.router.navigate([`tickets/${Navigation.NEW}/edit`]).then(() => {
            this.nav.freshNavigation;
            this.ticketService.select(newTicket);
            this.ticketSelected.emit(Navigation.NEW);
        });
    }

    relaySingleChoice(choice: SingleChoice): void {
        this.nav.startChoice(choice);
    }

    get actionLabelerByService(): Labeler {
        return this.multiActionService;
    }

    get actionIdObserver(): Partial<Observer<number>> {
        return {
            next: (id) => {
                this.ticketService.selectMultiActionId(id);
            },
        };
    }

    get actionUrlObserver(): Observable<string> {
        return this.ticketService.selectedMultiActionUri;
    }

    getStartRow(branchId: number): number {
        return this.getCurrentPage(branchId) * this.pageSize;
    }

    getEndRow(branchId: number): number {
        const start = this.getStartRow(branchId);
        const tickets = this.getTickets(branchId);
        return start + tickets.length;
    }

    getPaginationButtons(branchId: number): (number | string)[] {
        const total = this.getTotalPages(branchId);
        if (total <= 10) {
            return Array.from({ length: total }, (_, i) => i);
        }
        const current = this.getCurrentPage(branchId);
        const pages: (number | string)[] = [0, 1, 2];
        if (current > 4) pages.push("...");
        for (let i = Math.max(3, current - 2); i <= Math.min(total - 4, current + 2); i++) {
            pages.push(i);
        }
        if (current < total - 5) pages.push("...");
        pages.push(total - 3, total - 2, total - 1);
        return pages;
    }
}
